import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import PageHeader from "../../components/ui/PageHeader";
import {
  deleteFlow,
  deleteTemplate,
  getFlows,
  getConsentSummary,
  getSends,
  getTemplates,
  MailFlow,
  MailSend,
  MailTemplate,
  MailTrigger,
  previewTemplate,
  saveFlow,
  saveTemplate,
  sendNow,
  uploadTemplateImage,
} from "../../service/MarketingService";
import { showSuccessToast } from "../../utils/Toast";
import logo from "../../asset/logo/LOGO_ORANGE.svg";
import TemplateEditor, { blocksOf } from "./TemplateEditor";
import { asClient, MailClient } from "./mailClients";
import { orangeButton, outlinedButton, StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";

const TRIGGERS: MailTrigger[] = ["tenantCreated", "noBreefYet", "draftNotSent", "noBreefForDays", "subscriptionEnding", "breefCompleted", "paymentFailed", "subscriptionCancelled", "breefEndingSoon", "paymentSucceeded", "firstBreefCompleted", "signupNotCompleted"];
const emptyTemplate = (): MailTemplate => ({ name: "", subject: "", preheader: "", body: "", blocks: [] });
const emptyFlow = (templateId: string): MailFlow => ({ name: "", kind: "marketing", trigger: "tenantCreated", waitDays: 0, templateId, active: false });
const date = (value?: string) => (value ? new Date(value).toLocaleString("da-DK") : "-");

/**
 * Mails (superadmin): the mails Breefer writes to its own customers. The template is the text, the flow decides
 * when it goes out, and the log shows what went. Participants never get these.
 */
export default function Mails() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [flows, setFlows] = useState<MailFlow[]>([]);
  const [sends, setSends] = useState<MailSend[]>([]);
  const [consent, setConsent] = useState<{ customers: number; consented: number; unsubscribed: number; missing: number } | null>(null);
  const [editing, setEditing] = useState<MailTemplate | null>(null);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [flowEditing, setFlowEditing] = useState<MailFlow | null>(null);
  const [sending, setSending] = useState<MailTemplate | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [client, setClient] = useState<MailClient>("appleMail");

  const load = () => {
    getTemplates().then(setTemplates);
    getFlows().then(setFlows);
    getSends().then(setSends);
    getConsentSummary().then(setConsent);
  };
  useEffect(load, []);

  // The preview follows what is typed, a moment behind
  useEffect(() => {
    if (!editing) return undefined;
    const timer = setTimeout(() => {
      previewTemplate(editing).then(setPreview);
    }, 400);
    return () => clearTimeout(timer);
  }, [editing]);

  const templateName = (id: string) => templates.find((template) => template.id === id)?.name || "-";
  // The logo is attached to the real mail; in the preview it comes from the admin panel's own copy
  const previewHtml = useMemo(() => (preview ? asClient(preview.html.replace(/cid:breeferlogo/g, logo), client) : ""), [preview, client]);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    const saved = await saveTemplate(editing);
    setBusy(false);
    if (saved) {
      showSuccessToast(t("mails.saved"), isMobile);
      setEditing(null);
      load();
    }
  };

  const removeTemplate = async (template: MailTemplate) => {
    if (!template.id || !window.confirm(t("mails.confirmDelete", { name: template.name }))) return;
    if (await deleteTemplate(template.id)) load();
  };

  const saveTheFlow = async () => {
    if (!flowEditing) return;
    setBusy(true);
    const saved = await saveFlow(flowEditing);
    setBusy(false);
    if (saved) {
      showSuccessToast(t("mails.flowSaved"), isMobile);
      setFlowEditing(null);
      load();
    }
  };

  const toggleFlow = async (flow: MailFlow) => {
    await saveFlow({ ...flow, active: !flow.active });
    load();
  };

  const send = async (audience: "all" | "test") => {
    if (!sending?.id) return;
    if (audience === "all" && !window.confirm(t("mails.confirmSendAll"))) return;
    setBusy(true);
    const result = await sendNow(sending.id, audience, testEmail);
    setBusy(false);
    if (result) {
      showSuccessToast(t("mails.sent", { count: result.sent }), isMobile);
      setSending(null);
      load();
    }
  };

  const upload = async (file: File) => {
    setBusy(true);
    const url = await uploadTemplateImage(file);
    setBusy(false);
    return url;
  };

  return (
    <Layout>
      <PageHeader title={t("mails.title")} description={t("mails.intro")} />

      {/* Consent is the ceiling on everything below: no yes, no mail */}
      {consent && (
        <Box sx={{ width: "100%", mb: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
          <Typography variant="body2">
            {t("mails.consent", consent)}
            {consent.missing > 0 ? ` ${t("mails.consentMissing", consent)}` : ""}
          </Typography>
        </Box>
      )}

      <Box sx={{ width: "100%", mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">{t("mails.templates")}</Typography>
        <Button variant="contained" sx={orangeButton} onClick={() => setEditing(emptyTemplate())}>
          {t("mails.newTemplate")}
        </Button>
      </Box>
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("mails.name")}</StyledTableCell>
              <StyledTableCell>{t("mails.subject")}</StyledTableCell>
              <StyledTableCell>{t("mails.usedIn")}</StyledTableCell>
              <StyledTableCell>{t("mails.changed")}</StyledTableCell>
              <StyledTableCell align="right">{t("mails.actions")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <StyledTableRow key={template.id}>
                <TableCell>{template.name}</TableCell>
                <TableCell>{template.subject}</TableCell>
                <TableCell>
                  {/* The flows that send this template; a grey chip is a flow that is turned off */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {flows
                      .filter((flow) => flow.templateId === template.id)
                      .map((flow) => (
                        <Tooltip key={flow.id} title={`${t(`mails.triggers.${flow.trigger}`)} · ${flow.waitDays ? t("mails.waitDays", { days: flow.waitDays }) : t("mails.straightAway")} · ${flow.active ? t("mails.on") : t("mails.off")}`}>
                          <Chip size="small" label={flow.name} color={flow.active ? "primary" : "default"} variant={flow.active ? "filled" : "outlined"} onClick={() => setFlowEditing(flow)} />
                        </Tooltip>
                      ))}
                    {!flows.some((flow) => flow.templateId === template.id) && (
                      <Typography variant="caption" color="text.secondary">
                        {t("mails.notUsed")}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {date(template.updatedAt)}
                  {template.updatedBy ? ` · ${template.updatedBy}` : ""}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Button size="small" variant="outlined" sx={outlinedButton} onClick={() => setEditing({ ...template, blocks: blocksOf(template) })}>
                    {t("mails.edit")}
                  </Button>{" "}
                  <Button size="small" variant="outlined" sx={outlinedButton} onClick={() => { setSending(template); setTestEmail(""); }}>
                    {t("mails.send")}
                  </Button>{" "}
                  <Button size="small" variant="outlined" sx={outlinedButton} onClick={() => removeTemplate(template)}>
                    {t("mails.delete")}
                  </Button>
                </TableCell>
              </StyledTableRow>
            ))}
            {!templates.length && (
              <TableRow>
                <TableCell colSpan={5}>{t("mails.noTemplates")}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <Box sx={{ width: "100%", mt: 4, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h6">{t("mails.flows")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("mails.flowsHelp")}
          </Typography>
        </Box>
        <Button variant="contained" sx={orangeButton} disabled={!templates.length} onClick={() => setFlowEditing(emptyFlow(templates[0]?.id || ""))}>
          {t("mails.newFlow")}
        </Button>
      </Box>
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("mails.name")}</StyledTableCell>
              <StyledTableCell>{t("mails.kind")}</StyledTableCell>
              <StyledTableCell>{t("mails.trigger")}</StyledTableCell>
              <StyledTableCell>{t("mails.wait")}</StyledTableCell>
              <StyledTableCell>{t("mails.template")}</StyledTableCell>
              <StyledTableCell>{t("mails.reach")}</StyledTableCell>
              <StyledTableCell>{t("mails.lastRun")}</StyledTableCell>
              <StyledTableCell align="right">{t("mails.actions")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flows.map((flow) => (
              <StyledTableRow key={flow.id}>
                <TableCell>{flow.name}</TableCell>
                <TableCell>{t(`mails.kinds.${flow.kind || "marketing"}`)}</TableCell>
                <TableCell>{t(`mails.triggers.${flow.trigger}`)}</TableCell>
                <TableCell>{flow.waitDays ? t("mails.waitDays", { days: flow.waitDays }) : t("mails.straightAway")}</TableCell>
                <TableCell>{templateName(flow.templateId)}</TableCell>
                <TableCell>{t("mails.customers", { count: flow.reach ?? 0 })}</TableCell>
                <TableCell>
                  {date(flow.lastRunAt)}
                  {flow.lastRunAt ? ` · ${t("mails.customers", { count: flow.lastSentCount ?? 0 })}` : ""}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <FormControlLabel
                    control={<Switch size="small" checked={Boolean(flow.active)} onChange={() => toggleFlow(flow)} />}
                    label={flow.active ? t("mails.on") : t("mails.off")}
                    sx={{ mr: 1 }}
                  />
                  <Button size="small" variant="outlined" sx={outlinedButton} onClick={() => setFlowEditing(flow)}>
                    {t("mails.edit")}
                  </Button>{" "}
                  <Button
                    size="small"
                    variant="outlined"
                    sx={outlinedButton}
                    onClick={async () => {
                      if (flow.id && window.confirm(t("mails.confirmDeleteFlow", { name: flow.name }))) {
                        await deleteFlow(flow.id);
                        load();
                      }
                    }}
                  >
                    {t("mails.delete")}
                  </Button>
                </TableCell>
              </StyledTableRow>
            ))}
            {!flows.length && (
              <TableRow>
                <TableCell colSpan={7}>{t("mails.noFlows")}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <Box sx={{ width: "100%", mt: 4, mb: 1 }}>
        <Typography variant="h6">{t("mails.log")}</Typography>
      </Box>
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("mails.when")}</StyledTableCell>
              <StyledTableCell>{t("mails.to")}</StyledTableCell>
              <StyledTableCell>{t("mails.subject")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sends.slice(0, 25).map((row) => (
              <StyledTableRow key={row.id}>
                <TableCell>{date(row.sentAt)}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.subject}</TableCell>
              </StyledTableRow>
            ))}
            {!sends.length && (
              <TableRow>
                <TableCell colSpan={3}>{t("mails.noSends")}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {/* The template: the elements on the left, the mail itself as the canvas, the chosen element's settings on the right */}
      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="lg">
        <DialogTitle>{editing?.id ? t("mails.editTemplate") : t("mails.newTemplate")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
            <Box sx={{ flex: "1 1 100%" }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                <TextField margin="dense" sx={{ flex: "1 1 200px" }} label={t("mails.name")} value={editing?.name ?? ""} onChange={(event) => editing && setEditing({ ...editing, name: event.target.value })} />
                <TextField margin="dense" sx={{ flex: "1 1 240px" }} label={t("mails.subject")} value={editing?.subject ?? ""} onChange={(event) => editing && setEditing({ ...editing, subject: event.target.value })} />
                <TextField
                  margin="dense"
                  sx={{ flex: "1 1 240px" }}
                  label={t("mails.preheader")}
                  helperText={t("mails.preheaderHelp")}
                  value={editing?.preheader ?? ""}
                  onChange={(event) => editing && setEditing({ ...editing, preheader: event.target.value })}
                />
              </Box>
              {editing && <TemplateEditor value={editing} onChange={setEditing} uploadImage={upload} busy={busy} previewHtml={previewHtml} client={client} onClientChange={setClient} />}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} variant="outlined" sx={outlinedButton}>
            {t("mails.cancel")}
          </Button>
          <Button onClick={save} variant="contained" sx={orangeButton} disabled={busy}>
            {t("mails.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* The flow */}
      <Dialog open={Boolean(flowEditing)} onClose={() => setFlowEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{flowEditing?.id ? t("mails.editFlow") : t("mails.newFlow")}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" fullWidth label={t("mails.name")} value={flowEditing?.name ?? ""} onChange={(event) => flowEditing && setFlowEditing({ ...flowEditing, name: event.target.value })} />
          <TextField
            margin="dense"
            fullWidth
            select
            label={t("mails.kind")}
            value={flowEditing?.kind ?? "marketing"}
            onChange={(event) => flowEditing && setFlowEditing({ ...flowEditing, kind: event.target.value as "service" | "marketing" })}
          >
            <MenuItem value="service">{t("mails.kinds.service")}</MenuItem>
            <MenuItem value="marketing">{t("mails.kinds.marketing")}</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {t(`mails.kindHelp.${flowEditing?.kind ?? "marketing"}`)}
          </Typography>
          <TextField
            margin="dense"
            fullWidth
            select
            label={t("mails.trigger")}
            value={flowEditing?.trigger ?? "tenantCreated"}
            onChange={(event) => flowEditing && setFlowEditing({ ...flowEditing, trigger: event.target.value as MailTrigger })}
          >
            {TRIGGERS.map((trigger) => (
              <MenuItem key={trigger} value={trigger}>
                {t(`mails.triggers.${trigger}`)}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {t(`mails.triggerHelp.${flowEditing?.trigger ?? "tenantCreated"}`)}
          </Typography>
          <TextField
            margin="dense"
            fullWidth
            type="number"
            inputProps={{ min: 0, max: 365 }}
            label={t("mails.wait")}
            helperText={t("mails.waitHelp")}
            value={flowEditing?.waitDays ?? 0}
            onChange={(event) => flowEditing && setFlowEditing({ ...flowEditing, waitDays: Number(event.target.value) })}
          />
          <TextField
            margin="dense"
            fullWidth
            select
            label={t("mails.template")}
            value={flowEditing?.templateId ?? ""}
            onChange={(event) => flowEditing && setFlowEditing({ ...flowEditing, templateId: event.target.value })}
          >
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={Boolean(flowEditing?.active)} onChange={(event) => flowEditing && setFlowEditing({ ...flowEditing, active: event.target.checked })} />}
            label={t("mails.activeHelp")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlowEditing(null)} variant="outlined" sx={outlinedButton}>
            {t("mails.cancel")}
          </Button>
          <Button onClick={saveTheFlow} variant="contained" sx={orangeButton} disabled={busy || !flowEditing?.templateId}>
            {t("mails.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send by hand */}
      <Dialog open={Boolean(sending)} onClose={() => setSending(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t("mails.sendTitle", { name: sending?.name ?? "" })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("mails.sendHelp")}
          </Typography>
          <TextField margin="dense" fullWidth type="email" label={t("mails.testEmail")} value={testEmail} onChange={(event) => setTestEmail(event.target.value)} />
          <Button fullWidth variant="outlined" sx={{ ...outlinedButton, mt: 1 }} disabled={busy || !testEmail} onClick={() => send("test")}>
            {t("mails.sendTest")}
          </Button>
          <Button fullWidth variant="contained" sx={{ ...orangeButton, mt: 2 }} disabled={busy} onClick={() => send("all")}>
            {t("mails.sendAll")}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSending(null)} variant="outlined" sx={outlinedButton}>
            {t("mails.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
